import React, { useCallback } from 'react';
import { SETTING } from 'interfaces/settings';
import { useAutodialerSettings } from '../../hooks/useAutodialerSettings';
import { Typography } from 'interfaces/typography';
import { CustomDropdown, CleanSelectOption } from 'utils/CommonUIComponents';

export function TimeRangeControl() {
    const { getValueForSetting, updateSetting, getDefaultValueForSetting } = useAutodialerSettings();
    
    // Get current values (in hours 0-24)
    const minValue = getValueForSetting(SETTING.LOCAL_CALL_TIME_MIN) as number || getDefaultValueForSetting(SETTING.LOCAL_CALL_TIME_MIN) as number;
    const maxValue = getValueForSetting(SETTING.LOCAL_CALL_TIME_MAX) as number || getDefaultValueForSetting(SETTING.LOCAL_CALL_TIME_MAX) as number;
    
    // Generate time options (0-23 hours)
    const timeOptions: CleanSelectOption[] = Array.from({ length: 24 }, (_, i) => {
        const period = i >= 12 ? 'PM' : 'AM';
        const displayHours = i === 0 ? 12 : i > 12 ? i - 12 : i;
        return {
            value: i,
            label: `${displayHours}:00 ${period}`
        };
    });
    
    const handleMinChange = useCallback((newMin: string | number) => {
        const minNum = typeof newMin === 'string' ? parseInt(newMin) : newMin;
        const clampedValue = Math.min(minNum, maxValue - 1);
        updateSetting(SETTING.LOCAL_CALL_TIME_MIN, clampedValue);
        if (clampedValue >= maxValue) {
            updateSetting(SETTING.LOCAL_CALL_TIME_MAX, Math.min(clampedValue + 1, 23));
        }
    }, [maxValue, updateSetting]);
    
    const handleMaxChange = useCallback((newMax: string | number) => {
        const maxNum = typeof newMax === 'string' ? parseInt(newMax) : newMax;
        // Ensure max doesn't go below min
        const clampedValue = Math.max(maxNum, minValue + 1);
        updateSetting(SETTING.LOCAL_CALL_TIME_MAX, clampedValue);
        
        // If max is now <= min, adjust min
        if (clampedValue <= minValue) {
            updateSetting(SETTING.LOCAL_CALL_TIME_MIN, Math.max(clampedValue - 1, 0));
        }
    }, [minValue, updateSetting]);

    // Create options with disabled states
    const startOptions = timeOptions.slice(0, 23).map(option => ({
        ...option,
        disabled: (typeof option.value === 'string' ? parseInt(option.value) : option.value) >= maxValue
    }));
    
    const endOptions = timeOptions.slice(1).map(option => ({
        ...option,
        disabled: (typeof option.value === 'string' ? parseInt(option.value) : option.value) <= minValue
    }));

    return (
        <div className="flex items-center gap-4 flex-1 h-full">
            {/* Start Time Selector */}
            <div className="flex items-center gap-2">
                <Typography variant="smallParagraph" color="#6b7280" fontWeight="500" className="whitespace-nowrap">
                    From
                </Typography>
                <CustomDropdown
                    value={minValue}
                    onChange={handleMinChange}
                    options={startOptions}
                    className="min-w-[100px]"
                />
            </div>
            
            {/* End Time Selector */}
            <div className="flex items-center gap-2">
                <Typography variant="smallParagraph" color="#6b7280" fontWeight="500" className="whitespace-nowrap">
                    to
                </Typography>
                <CustomDropdown
                    value={maxValue}
                    onChange={handleMaxChange}
                    options={endOptions}
                    className="min-w-[100px]"
                />
            </div>
        </div>
    );
} 